/**
 * Focus trap + restore for dialogs/drawers (WCAG 2.4.3, 3.2.1, 1.3.1).
 *
 * Usage:
 *   const dialogRef = useDialogFocus();
 *   return <div ref={dialogRef} role="dialog" ...>...</div>;
 *
 * On mount: focuses the first focusable inside the dialog. While open: Tab
 * cycles only within. On unmount: restores focus to whatever was focused
 * before the dialog opened (typically the trigger button).
 */
import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useDialogFocus<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const lastFocused = document.activeElement as HTMLElement | null;
    function focusables(): HTMLElement[] {
      if (!ref.current) return [];
      return Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    }
    // Defer to next tick so refs in render tree settle before we query.
    const timer = setTimeout(() => {
      const list = focusables();
      const first = list.find((el) => el.getAttribute('data-autofocus') !== null) ?? list[0];
      first?.focus();
    }, 0);
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active && ref.current && !ref.current.contains(active)) {
        // Focus escaped — pull it back to the dialog
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey, true);
      // Restore focus to where it was before open. Use a microtask so React
      // has unmounted children before we attempt to focus.
      queueMicrotask(() => {
        if (lastFocused && document.contains(lastFocused)) {
          try {
            lastFocused.focus();
          } catch {
            /* element no longer focusable — ignore */
          }
        }
      });
    };
  }, []);
  return ref;
}
